/**
 * AdminAudioStatus — voice-recording launch dashboard.
 *
 * Lists every voice file the app expects, marks ✅ uploaded / ❌ missing,
 * and lets Michael drag-and-drop a recording straight into the right
 * folder — no SSH / git push required.
 *
 * Route: /admin/audio   (admin-only)
 * Backend: /api/admin/audio/status, /upload/:id, /file/:id (DELETE)
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { AdminLayout } from "../AdminPages";
import {
  ArrowLeft, Check, X, Upload, Trash2, Play, Pause, AlertTriangle, Mic,
  CheckCircle2, Clock, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

const priorityColor = {
  P0: { bg: "bg-red-50", ring: "ring-red-200", text: "text-red-700", dot: "bg-red-500" },
  P1: { bg: "bg-amber-50", ring: "ring-amber-200", text: "text-amber-700", dot: "bg-amber-500" },
  P2: { bg: "bg-slate-50", ring: "ring-slate-200", text: "text-slate-700", dot: "bg-slate-400" },
};

export default function AdminAudioStatus() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({}); // { [id]: true }
  const [playing, setPlaying] = useState(null);
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API}/api/admin/audio/status`, { headers: auth() });
      if (!res.ok) throw new Error(`${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(`Failed to load audio status: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== "admin") return;
    fetchStatus();
  }, [user]);

  // Admin gate — AdminLayout also enforces this but we guard early so
  // the fetch effect doesn't fire for non-admins. Redirect happens in
  // an effect to avoid "navigate during render" warnings.
  useEffect(() => {
    if (user && user.role !== "admin") navigate("/");
  }, [user, navigate]);

  if (!user) return null;
  if (user.role !== "admin") return null;

  const handleUpload = async (id, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [id]: true }));
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API}/api/admin/audio/upload/${id}`, {
        method: "POST",
        headers: auth(),
        body: form,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || res.statusText);
      await fetchStatus();
    } catch (e) {
      alert(`Upload failed: ${e.message}`);
    } finally {
      setUploading((u) => { const n = { ...u }; delete n[id]; return n; });
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this recording? You can re-upload immediately.")) return;
    try {
      const res = await fetch(`${API}/api/admin/audio/file/${id}`, { method: "DELETE", headers: auth() });
      if (!res.ok) throw new Error(`${res.status}`);
      await fetchStatus();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    }
  };

  const togglePlay = (id, filename) => {
    if (playing === id) { setPlaying(null); return; }
    // Strip the `split/` prefix or `../integration/nights/` traversal for URL
    const clean = filename.replace(/^\.\.\//, "");
    const url = clean.startsWith("integration/nights")
      ? `${API}/api/audio/${clean}`
      : `${API}/api/audio/voice/${clean}`;
    const audio = new Audio(url);
    audio.play().catch(() => {});
    audio.onended = () => setPlaying(null);
    setPlaying(id);
    // Stop after 15s to avoid long plays in the admin UI
    setTimeout(() => { try { audio.pause(); } catch {} }, 15000);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 text-center text-[#78716C]">
          <Loader2 className="inline-block animate-spin" /> Loading audio status…
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="p-4 border border-red-300 bg-red-50 text-red-700 rounded">
            {error}
          </div>
        </div>
      </AdminLayout>
    );
  }

  const categories = [...new Set(data.items.map((i) => i.category))];
  const summary = data.summary;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-audio-page">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl text-[#292524]">Voice Recordings</h2>
            <p className="text-sm text-[#78716C]">Upload & manage Michael's voice files for launch</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <a
              href={`${API}/api/admin/static/missing_audio_pack.pdf`}
              target="_blank"
              rel="noreferrer"
              className="text-sm bg-gradient-to-r from-[#A07840] to-[#7A5A2E] text-white px-4 py-2 rounded-lg hover:opacity-90 flex items-center gap-2 font-medium"
              data-testid="download-missing-audio-pack"
              title="Only the scripts whose audio files aren't yet recorded"
            >
              ◐ Missing Audios PDF
            </a>
            <a
              href={`${API}/api/admin/static/master_recording_pack.pdf`}
              target="_blank"
              rel="noreferrer"
              className="text-sm bg-gradient-to-r from-[#0E1A2C] to-[#1F3349] text-white px-4 py-2 rounded-lg hover:opacity-90 flex items-center gap-2 font-medium"
              data-testid="download-master-recording-pack"
            >
              ★ Master Recording Pack
            </a>
            <a
              href={`${API}/api/admin/static/voice_recording_pack.pdf`}
              target="_blank"
              rel="noreferrer"
              className="text-sm bg-[#0D1B2A] text-white px-4 py-2 rounded-lg hover:bg-[#1F3148] flex items-center gap-2"
              data-testid="download-recording-pack-pdf"
            >
              Recording pack PDF
            </a>
            <a
              href={`${API}/api/admin/static/affirmations_recording_script.pdf`}
              target="_blank"
              rel="noreferrer"
              className="text-sm bg-amber-700 text-white px-4 py-2 rounded-lg hover:bg-amber-800 flex items-center gap-2"
              data-testid="download-affirmations-script-pdf"
            >
              Affirmations script PDF
            </a>
          </div>
        </div>

        {/* Priority summary */}
        <div className="grid grid-cols-3 gap-3">
          {["P0", "P1", "P2"].map((p) => {
            const s = summary[p] || { total: 0, uploaded: 0 };
            const pct = s.total ? Math.round((s.uploaded / s.total) * 100) : 100;
            const colors = priorityColor[p];
            return (
              <div
                key={p}
                className={`${colors.bg} ${colors.ring} ring-1 rounded-xl p-4`}
                data-testid={`summary-${p.toLowerCase()}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`${colors.text} text-sm font-medium`}>
                      {p} — {p === "P0" ? "Blocks launch" : p === "P1" ? "Strongly recommended" : "Nice to have"}
                    </p>
                    <p className="text-2xl font-heading mt-1">{s.uploaded}/{s.total}</p>
                  </div>
                  <div className={`text-2xl font-heading ${colors.text}`}>{pct}%</div>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-white/70 overflow-hidden">
                  <div className={`h-full ${colors.dot}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Category groups */}
        {categories.map((cat) => {
          const items = data.items.filter((i) => i.category === cat);
          return (
            <div key={cat} className="space-y-2">
              <h3 className="font-heading text-lg text-[#292524] mt-6">{cat}</h3>
              <div className="space-y-2">
                {items.map((item) => (
                  <AudioRow
                    key={item.id}
                    item={item}
                    uploading={!!uploading[item.id]}
                    playing={playing === item.id}
                    onUpload={(file) => handleUpload(item.id, file)}
                    onDelete={() => handleDelete(item.id)}
                    onTogglePlay={() => togglePlay(item.id, item.filename)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </AdminLayout>
  );
}

function AudioRow({ item, uploading, playing, onUpload, onDelete, onTogglePlay }) {
  const [dragOver, setDragOver] = useState(false);
  const colors = priorityColor[item.priority] || priorityColor.P2;

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) onUpload(file);
  };

  return (
    <div
      className={`flex flex-col md:flex-row md:items-center gap-3 p-3 rounded-lg border transition-colors ${
        dragOver
          ? "border-[#4A6741] bg-green-50"
          : item.uploaded
            ? "border-[#E6E2DD] bg-white"
            : "border-dashed border-[#C7C2BB] bg-[#FAF9F7]"
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      data-testid={`audio-row-${item.id}`}
    >
      {/* Status dot + id */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {item.uploaded ? (
          <CheckCircle2 size={20} className="text-green-600 shrink-0" />
        ) : (
          <Clock size={20} className="text-amber-500 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-[#292524] truncate">{item.id}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${colors.bg} ${colors.text} uppercase tracking-wider font-semibold`}>
              {item.priority}
            </span>
            {item.uploaded && (
              <span className="text-xs text-[#78716C]">{item.size_kb} KB</span>
            )}
          </div>
          <p className="text-xs text-[#78716C] truncate mt-0.5">{item.description}</p>
          <p className="text-[10px] text-[#A8A29E] font-mono truncate mt-0.5">{item.filename}</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        {item.uploaded && (
          <>
            <button
              onClick={onTogglePlay}
              className="px-3 py-1.5 rounded-md border border-[#E6E2DD] text-sm text-[#78716C] hover:text-[#292524] hover:border-[#C7C2BB]"
              data-testid={`play-${item.id}`}
            >
              {playing ? <Pause size={14} /> : <Play size={14} />}
            </button>
            <button
              onClick={onDelete}
              className="px-3 py-1.5 rounded-md border border-[#E6E2DD] text-sm text-red-600 hover:bg-red-50 hover:border-red-200"
              data-testid={`delete-${item.id}`}
            >
              <Trash2 size={14} />
            </button>
          </>
        )}
        <label
          className={`px-3 py-1.5 rounded-md text-sm cursor-pointer flex items-center gap-1.5 ${
            uploading
              ? "bg-gray-200 text-gray-500 cursor-wait"
              : item.uploaded
                ? "border border-[#E6E2DD] text-[#78716C] hover:text-[#292524] hover:border-[#C7C2BB]"
                : "bg-[#4A6741] text-white hover:bg-[#3d5635]"
          }`}
          data-testid={`upload-btn-${item.id}`}
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? "Uploading…" : item.uploaded ? "Replace" : "Upload"}
          <input
            type="file"
            accept=".mp3,.m4a,.wav,audio/mpeg,audio/mp4,audio/wav"
            className="hidden"
            disabled={uploading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUpload(file);
              e.target.value = "";
            }}
          />
        </label>
      </div>
    </div>
  );
}
